import { useQueryClient } from '@tanstack/react-query';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import { GoogleSignInButton } from '@/components/auth/google-sign-in-button';
import { ResendVerification } from '@/components/auth/resend-verification';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { useSession } from '@/hooks/use-session';
import { onIdentityChanged } from '@/lib/session-change';
import { trpc } from '@/lib/trpc';

export const Route = createFileRoute('/register')({
  component: RegisterPage,
});

function RegisterPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useSession();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [displayName, setDisplayName] = useState('');
  /**
   * Set when the server says a confirmation mail is on its way, which is what turns this page
   * into the check-your-inbox card instead of navigating away.
   *
   * It is state here rather than a read of the mutation's own `data`, because the card has to
   * survive the resend control's own mutation re-rendering this component.
   */
  const [awaitingConfirmation, setAwaitingConfirmation] = useState(false);

  const onSuccess = async (data?: { emailVerificationRequired?: boolean }) => {
    // A new account — or a guest upgraded into one — is a different identity to everything
    // this client cached under the old one.
    await onIdentityChanged(queryClient);
    // THE ACCOUNT IS ALREADY SIGNED IN EITHER WAY. The gate is at login, so a player who has
    // just registered can go and play; what the card below is for is telling them what has to
    // happen before they can come *back*, and giving them the resend if the mail never lands.
    if (data?.emailVerificationRequired) {
      setAwaitingConfirmation(true);
      return;
    }
    // The lobby, for `login.tsx`'s reason: creating an account — or upgrading a guest one — is
    // not a request to be dropped into a game. It is one click from here to a room.
    navigate({ to: '/' });
  };

  const register = trpc.auth.register.useMutation({ onSuccess });
  // A guest already has a row, a name and stats. Registering from that session upgrades it in
  // place rather than stranding the run behind a second account.
  const upgradeGuest = trpc.auth.upgradeGuest.useMutation({ onSuccess });

  const isUpgrade = Boolean(user?.isGuest);
  const active = isUpgrade ? upgradeGuest : register;

  if (awaitingConfirmation) {
    return (
      <div className="flex flex-1 items-center justify-center p-4">
        <Card className="w-full max-w-sm">
          <CardHeader>
            <CardTitle>Check your inbox</CardTitle>
            <CardDescription>
              We have sent a confirmation link to {email}. Open it and your account is ready to sign
              in from anywhere.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground">
              You are signed in already, so you can play right now — the link is what you need
              before you can sign in again later.
            </p>
            <ResendVerification email={email} />
            <div className="mt-4 flex flex-col gap-2">
              <Button className="w-full" onClick={() => navigate({ to: '/' })}>
                Play now
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="flex flex-1 items-center justify-center p-4">
      <Card className="w-full max-w-sm">
        <CardHeader>
          <CardTitle>{isUpgrade ? 'Keep your run' : 'Create an account'}</CardTitle>
          <CardDescription>
            {isUpgrade
              ? `Turn ${user?.displayName} into a real account — your stats come with you, and you finally pick a name.`
              : 'Your mass, kills and duels follow you between devices.'}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form
            className="space-y-4"
            onSubmit={(event) => {
              event.preventDefault();
              const trimmed = displayName.trim();
              // REQUIRED ON BOTH PATHS. An upgrade used to let a guest keep the name they had,
              // but that name is a minted `Chomp-xxxxxxxx` nobody chose — and this is the moment
              // it would follow them onto the world leaderboards, which is where a registered
              // player's name is read. Picking one is the price of the account.
              if (isUpgrade) {
                upgradeGuest.mutate({ email, password, displayName: trimmed });
              } else {
                register.mutate({ email, password, displayName: trimmed });
              }
            }}
          >
            <div className="space-y-1.5">
              <label htmlFor="displayName" className="text-sm font-medium">
                Username
              </label>
              <Input
                id="displayName"
                value={displayName}
                onChange={(event) => setDisplayName(event.target.value)}
                required
                maxLength={16}
                autoComplete="nickname"
                placeholder="Chomper"
              />
              <p className="text-xs text-muted-foreground">
                {isUpgrade
                  ? 'Pick the name you want on the leaderboards — it replaces the one we gave you.'
                  : 'This is the name everyone sees. Unique, and yours.'}
              </p>
            </div>

            <div className="space-y-1.5">
              <label htmlFor="email" className="text-sm font-medium">
                Email
              </label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                required
                autoComplete="email"
                placeholder="you@example.com"
              />
            </div>

            <div className="space-y-1.5">
              <label htmlFor="new-password" className="text-sm font-medium">
                Password
              </label>
              <Input
                id="new-password"
                type="password"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                required
                minLength={8}
                autoComplete="new-password"
              />
            </div>

            {active.error && (
              <p className="text-sm text-destructive" role="alert">
                {active.error.message}
              </p>
            )}

            <Button type="submit" disabled={active.isPending} className="w-full">
              {active.isPending ? 'Creating…' : isUpgrade ? 'Save my account' : 'Create account'}
            </Button>
          </form>

          <GoogleSignInButton returnTo="/" />

          <p className="mt-4 text-center text-sm text-muted-foreground">
            Already have an account?{' '}
            <Link to="/login" className="font-medium text-primary hover:underline">
              Sign in
            </Link>
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
